import { ChangeEvent, MouseEvent, useCallback, useRef, useState } from "react";
import * as s from "./styles";
import {
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Box,
  TableBody,
  TableHead,
  Paper,
  TableFooter,
  Tooltip
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React from "react";
import { CollapsibleData, TableCollapsibleProps } from "./types";
import { Loader } from "components/common/Loader";
import { Menu } from "components/common/Menu";
import { ELEMENT_IDS } from "../../../constants";

function getCollapsibleData<T>(
  row: T | undefined,
  key: keyof any
): CollapsibleData[] | undefined {
  const value = row?.[key];
  return Array.isArray(value) ? (value as CollapsibleData[]) : undefined;
}

// with `maxLength` the cell is limited to about that many characters and the
// rest is hidden behind an ellipsis by CSS, so copying still gets the full text
const CellValue = ({
  value,
  maxLength
}: {
  value: unknown;
  maxLength?: number;
}) =>
  maxLength ? (
    <s.TruncatedText title={String(value)} $maxLength={maxLength}>
      {value as React.ReactNode}
    </s.TruncatedText>
  ) : (
    <span title={String(value)}>{value as React.ReactNode}</span>
  );

export const TableCollapsible = <T extends { id: string }>({
  rows,
  columns,
  collapsibleDataColumns,
  collapsibleDataKey,
  toolbarItems,
  totalRow,
  isLoading,
  isSearchEnabled,
  searchString,
  onChangeSearch,
  itemWithIcon,
  tableTitle,
  collapsibleItemWithIcon,
  actions,
  collapsibleActions
}: TableCollapsibleProps<T>) => {
  // Rows are kept open by id, not by position, so search, sorting or a
  // refresh does not move the expanded state to another row. Any number of
  // rows can be open at once.
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);

  const toggleOpen = (rowId: string) => {
    setOpenRowIds((ids) =>
      ids.includes(rowId) ? ids.filter((id) => id !== rowId) : [...ids, rowId]
    );
  };

  const [search, setSearch] = useState("");
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onChangeSearch) {
        onChangeSearch(e);
      } else {
        setSearch(e.target.value);
      }
    },
    [onChangeSearch]
  );
  const filteredRows = search
    ? rows.filter((row) =>
        columns.some(
          (column) =>
            String(row[column.key])
              .toLowerCase()
              .indexOf(search.toLowerCase()) > -1
        )
      )
    : rows;

  const hasActions = Boolean(actions && actions.length > 0);
  const hasCollapsibleActions = Boolean(
    collapsibleActions && collapsibleActions.length > 0
  );

  const [isActionsMenuOpened, setIsActionsMenuOpened] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const actionsMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isCollapsibleActionsMenuOpened, setIsCollapsibleActionsMenuOpened] =
    useState(false);
  const [selectedCollapsibleItem, setSelectedCollapsibleItem] = useState<{
    id: string;
    parentId: string;
  } | null>(null);
  const collapsibleActionsMenuButtonRef = useRef<HTMLButtonElement | null>(
    null
  );

  const selectedItem = selectedItemId
    ? rows.find((row) => row.id === selectedItemId)
    : undefined;

  const selectedCollapsibleData =
    selectedCollapsibleItem && collapsibleDataKey
      ? getCollapsibleData(
          rows.find((row) => row.id === selectedCollapsibleItem.parentId),
          collapsibleDataKey
        )?.find((item) => item.id === selectedCollapsibleItem.id)
      : undefined;

  const handleActionsMenuButtonClick = useCallback(
    (id: string) => (e: MouseEvent<HTMLButtonElement>) => {
      actionsMenuButtonRef.current = e.currentTarget;
      setSelectedItemId(id);
      setIsActionsMenuOpened((isOpened) => !isOpened);
    },
    []
  );

  const handleActionsMenuClose = useCallback(() => {
    setIsActionsMenuOpened(false);
  }, []);

  const handleActionsMenuItemClick = useCallback(
    (callback: (id: string) => void) => () => {
      if (selectedItemId) {
        setIsActionsMenuOpened(false);
        callback(selectedItemId);
      }
    },
    [selectedItemId]
  );

  const handleCollapsibleActionsMenuButtonClick = useCallback(
    (id: string, parentId: string) => (e: MouseEvent<HTMLButtonElement>) => {
      collapsibleActionsMenuButtonRef.current = e.currentTarget;
      setSelectedCollapsibleItem({ id, parentId });
      setIsCollapsibleActionsMenuOpened((isOpened) => !isOpened);
    },
    []
  );

  const handleCollapsibleActionsMenuClose = useCallback(() => {
    setIsCollapsibleActionsMenuOpened(false);
  }, []);

  const handleCollapsibleActionsMenuItemClick = useCallback(
    (callback: (id: string, parentId: string) => void) => () => {
      if (selectedCollapsibleItem) {
        setIsCollapsibleActionsMenuOpened(false);
        callback(selectedCollapsibleItem.id, selectedCollapsibleItem.parentId);
      }
    },
    [selectedCollapsibleItem]
  );

  const menuBoundariesElement = document.getElementById(
    ELEMENT_IDS.CONTENT_CONTAINER
  );

  return (
    <s.Container>
      <s.Toolbar>
        {toolbarItems}
        {isSearchEnabled && (
          <s.SearchTextField
            value={onChangeSearch ? searchString : search}
            label={"Search"}
            slotProps={{
              htmlInput: { title: "Search" }
            }}
            onChange={handleSearchChange}
            size={"small"}
          />
        )}
      </s.Toolbar>
      {/* {tableTitle && <s.TitleRegion component="h4">{tableTitle}</s.TitleRegion>} */}
      {tableTitle && <s.Description>{tableTitle}</s.Description>}
      <Paper variant="outlined">
        <s.CustomTable stickyHeader data-testid={"table-container"}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {columns.map((column) => (
                <TableCell
                  key={column.key as string}
                  style={
                    column.width || column.isCentered
                      ? {
                          width: column.width ? column.width : "auto"
                          // textAlign: column.isCentered ? "center" : "left"
                        }
                      : {}
                  }
                >
                  {column.label}
                </TableCell>
              ))}
              {hasActions && <s.ActionsTableCell />}
            </TableRow>
          </TableHead>
          {!isLoading && (
            <>
              <TableBody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row, index) => {
                    const rowId = String(row.id ?? index);
                    const isOpen = openRowIds.includes(rowId);

                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell padding="checkbox">
                            {collapsibleDataKey && collapsibleDataColumns && (
                              <IconButton
                                size="small"
                                onClick={() => toggleOpen(rowId)}
                                aria-expanded={isOpen}
                                disabled={
                                  getCollapsibleData(row, collapsibleDataKey)
                                    ?.length === 0
                                }
                              >
                                {isOpen ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>

                          {columns.map((column) => (
                            <TableCell
                              key={String(column.key)}
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {itemWithIcon &&
                              itemWithIcon.column === column.key ? (
                                <s.IconContainer
                                // centered={Boolean(!itemWithIcon.withText)}
                                >
                                  <Tooltip
                                    title={itemWithIcon.tooltipText(row)}
                                    placement="top"
                                    arrow
                                  >
                                    <span>
                                      {itemWithIcon.getIcon(
                                        String(
                                          row[column.key as keyof typeof row]
                                        )
                                      )}
                                    </span>
                                  </Tooltip>
                                  {itemWithIcon.withText && (
                                    <s.TableCellTextWithMargin variant="body2">
                                      {String(
                                        row[column.key as keyof typeof row]
                                      )}
                                    </s.TableCellTextWithMargin>
                                  )}
                                </s.IconContainer>
                              ) : (
                                <CellValue
                                  value={row[column.key as keyof typeof row]}
                                  maxLength={column.maxLength}
                                />
                              )}

                              {/* {row[column.key as keyof typeof row]} */}
                            </TableCell>
                          ))}
                          {hasActions && (
                            <s.ActionsTableCell
                              padding={"checkbox"}
                              data-testid={"table-row-actions-menu-button"}
                            >
                              {actions?.some(
                                (action) =>
                                  !action.isDisabled || !action.isDisabled(row)
                              ) && (
                                <Tooltip
                                  title={"Actions"}
                                  placement={"top"}
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      onClick={handleActionsMenuButtonClick(
                                        row.id
                                      )}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </s.ActionsTableCell>
                          )}
                        </TableRow>
                        {collapsibleDataKey &&
                          collapsibleDataColumns &&
                          getCollapsibleData(row, collapsibleDataKey) &&
                          isOpen && (
                            <TableRow>
                              {/* the same cell as the expand arrow one, so the
                                nested first column lines up with the parent's */}
                              <TableCell padding="checkbox" />
                              <TableCell
                                // no side gaps, so the nested columns line up
                                // with the parent's ones
                                style={{ padding: 0 }}
                                colSpan={columns.length + (hasActions ? 1 : 0)}
                              >
                                <Collapse
                                  in={isOpen}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ marginY: 1 }}>
                                    <s.CollapsedTable size="small">
                                      <TableHead>
                                        <TableRow>
                                          {collapsibleDataColumns.map((col) => (
                                            <TableCell
                                              key={col.key}
                                              style={{
                                                textAlign: col.isCentered
                                                  ? "center"
                                                  : "left",
                                                width: col.width || "auto"
                                              }}
                                            >
                                              {col.label}
                                            </TableCell>
                                          ))}
                                          {hasCollapsibleActions && (
                                            <s.ActionsTableCell />
                                          )}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {getCollapsibleData(
                                          row,
                                          collapsibleDataKey
                                        )?.map((item: CollapsibleData, i) => (
                                          // <TableRow key={i}>
                                          //   {collapsibleDataColumns.map((col) => (
                                          //     <TableCell
                                          //       key={col.key}
                                          //       style={{ textAlign: "center" }}
                                          //     >
                                          //       {item[col.key]}
                                          //     </TableCell>
                                          //   ))}
                                          // </TableRow>
                                          <TableRow key={i}>
                                            {collapsibleDataColumns.map(
                                              (col) => (
                                                <TableCell
                                                  key={col.key}
                                                  style={{
                                                    textAlign: col.isCentered
                                                      ? "center"
                                                      : "left",
                                                    width: col.width || "auto"
                                                  }}
                                                >
                                                  {collapsibleItemWithIcon &&
                                                  collapsibleItemWithIcon.column ===
                                                    col.key ? (
                                                    <s.CollapsedIconContainer>
                                                      <Tooltip
                                                        title={collapsibleItemWithIcon.tooltipText(
                                                          item
                                                        )}
                                                        placement="top"
                                                        arrow
                                                      >
                                                        <span>
                                                          {collapsibleItemWithIcon.getIcon(
                                                            item,
                                                            col.key
                                                          )}
                                                        </span>
                                                      </Tooltip>
                                                      {collapsibleItemWithIcon.withText && (
                                                        <s.TableCellTextWithMargin variant="body2">
                                                          {String(
                                                            item[col.key]
                                                          )}
                                                        </s.TableCellTextWithMargin>
                                                      )}
                                                    </s.CollapsedIconContainer>
                                                  ) : (
                                                    <CellValue
                                                      value={item[col.key]}
                                                      maxLength={col.maxLength}
                                                    />
                                                  )}
                                                </TableCell>
                                              )
                                            )}
                                            {hasCollapsibleActions && (
                                              <s.CollapsedActionsTableCell
                                                padding={"checkbox"}
                                                data-testid={
                                                  "table-collapsible-row-actions-menu-button"
                                                }
                                              >
                                                {collapsibleActions?.some(
                                                  (action) =>
                                                    !action.isDisabled ||
                                                    !action.isDisabled(item)
                                                ) && (
                                                  <Tooltip
                                                    title={"Actions"}
                                                    placement={"top"}
                                                    arrow
                                                  >
                                                    <span>
                                                      <IconButton
                                                        onClick={handleCollapsibleActionsMenuButtonClick(
                                                          String(item.id),
                                                          row.id
                                                        )}
                                                      >
                                                        <MoreVertIcon />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                )}
                                              </s.CollapsedActionsTableCell>
                                            )}
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </s.CollapsedTable>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1 + (hasActions ? 1 : 0)}
                      data-testid={"table-cell-no-data"}
                    >
                      <s.NoDataText data-testid={"no-data-text"}>
                        No data
                      </s.NoDataText>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {totalRow && (
                <TableFooter>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    {columns.map((column) => (
                      <TableCell key={String(column.key)}>
                        <s.TableTotalCellText>
                          {String(totalRow[column.key as keyof T])}
                        </s.TableTotalCellText>
                      </TableCell>
                    ))}
                    {hasActions && <s.ActionsTableCell />}
                  </TableRow>
                </TableFooter>
              )}
            </>
          )}
        </s.CustomTable>
        {isLoading && (
          <s.LoaderContainer>
            <Loader text={"Loading data..."} />
          </s.LoaderContainer>
        )}
      </Paper>
      <Menu
        MenuListProps={{ "data-testid": "table-row-actions-menu-list" }}
        isOpened={isActionsMenuOpened}
        onClose={handleActionsMenuClose}
        anchorEl={actionsMenuButtonRef.current}
        boundariesElement={menuBoundariesElement}
        items={actions
          ?.filter(
            (action) =>
              (selectedItem &&
                action.isDisabled &&
                !action.isDisabled(selectedItem)) ||
              !action.isDisabled
          )
          .map((action) => ({
            label: action.label,
            onClick: handleActionsMenuItemClick(action.handler)
          }))}
      ></Menu>
      <Menu
        MenuListProps={{
          "data-testid": "table-collapsible-row-actions-menu-list"
        }}
        isOpened={isCollapsibleActionsMenuOpened}
        onClose={handleCollapsibleActionsMenuClose}
        anchorEl={collapsibleActionsMenuButtonRef.current}
        boundariesElement={menuBoundariesElement}
        items={collapsibleActions
          ?.filter(
            (action) =>
              (selectedCollapsibleData &&
                action.isDisabled &&
                !action.isDisabled(selectedCollapsibleData)) ||
              !action.isDisabled
          )
          .map((action) => ({
            label: action.label,
            onClick: handleCollapsibleActionsMenuItemClick(action.handler)
          }))}
      ></Menu>
    </s.Container>
  );
};
